/*
  NameSplitBackfillPanel — one-tap retro migration that splits the
  legacy ``full_name`` on every ``client_profiles`` doc into first /
  middle / surname. Safe by default (fill-blanks only); opt-in
  ``overwrite`` mode replaces existing split values as well. Preview
  first, then apply.

  Backend: POST /api/clients/backfill-name-split?dry_run&overwrite
*/
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Split, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function NameSplitBackfillPanel() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [overwrite, setOverwrite] = useState(false);

  async function runPreview() {
    setLoading(true);
    try {
      const r = await api.post(
        `/clients/backfill-name-split?dry_run=true&overwrite=${overwrite}`,
      );
      setPreview(r.data);
    } catch (e) {
      toast.error("Preview failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }

  async function runApply() {
    if (!preview || preview.would_update === 0) return;
    if (!window.confirm(
      `Split full_name on ${preview.would_update} client profile(s) into first / middle / surname?` +
      (overwrite ? "\n\nOVERWRITE MODE: existing split values will be replaced." : ""),
    )) return;
    setApplying(true);
    try {
      const r = await api.post(
        `/clients/backfill-name-split?overwrite=${overwrite}`,
      );
      toast.success(`Split ${r.data.updated} client profile(s)`);
      setPreview(r.data);
    } catch (e) {
      toast.error("Backfill failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setApplying(false);
    }
  }

  return (
    <Card className="p-4 space-y-4" data-testid="name-split-backfill-panel">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-800">
          <Split className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-zinc-900">
            Split existing client names
          </div>
          <div className="text-xs text-zinc-600 leading-relaxed">
            Retrofit the new First / Middle / Surname split onto client
            profiles that were created before the intake schema change.
            Safe mode (default) only fills blank fields — hand-edits
            survive. Preview first to review the split before applying.
          </div>
        </div>
      </div>

      <label className="flex items-center gap-2 text-xs text-zinc-700">
        <input
          type="checkbox"
          checked={overwrite}
          onChange={(e) => { setOverwrite(e.target.checked); setPreview(null); }}
          data-testid="name-split-overwrite"
        />
        Overwrite existing split values (destructive — clears hand-edits)
      </label>

      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={runPreview} disabled={loading}
                data-testid="name-split-preview">
          {loading ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Scanning…</>
                    : <>Preview split</>}
        </Button>
        <Button size="sm" onClick={runApply}
                disabled={!preview || preview.would_update === 0 || applying}
                data-testid="name-split-apply">
          {applying
            ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Applying…</>
            : <><PlayCircle className="h-3.5 w-3.5 mr-1" />
                Apply {preview ? `(${preview.would_update})` : ""}</>}
        </Button>
      </div>

      {preview && (
        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 text-xs space-y-1"
             data-testid="name-split-preview-result">
          <div className="text-zinc-800">
            Scanned <b>{preview.scanned}</b> profile(s) ·{" "}
            {preview.dry_run
              ? <>would update <b>{preview.would_update}</b></>
              : <>updated <b>{preview.updated}</b></>}
            {preview.overwrite && <span className="ml-1 text-red-700">· overwrite</span>}
          </div>
          {preview.sample?.length > 0 && (
            <div className="mt-2 space-y-1">
              <div className="text-2xs uppercase tracking-wide text-zinc-500">
                Sample (first {preview.sample.length})
              </div>
              <table className="w-full text-2xs font-mono-num">
                <thead className="text-zinc-500">
                  <tr>
                    <th className="text-left py-0.5">Full name</th>
                    <th className="text-left py-0.5">→ First</th>
                    <th className="text-left py-0.5">Middle</th>
                    <th className="text-left py-0.5">Surname</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.sample.map((r, i) => (
                    <tr key={i} className="border-t border-zinc-200 align-top"
                        data-testid={`name-split-sample-${i}`}>
                      <td className="py-0.5 pr-2 truncate max-w-[180px]" title={r.full_name}>{r.full_name}</td>
                      <td className="py-0.5 pr-2">{r.first_name || "—"}</td>
                      <td className="py-0.5 pr-2">{r.middle_name || "—"}</td>
                      <td className="py-0.5 pr-2">{r.last_name || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
